"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { ArrowLeft, FileCheck2, Download, Upload, Plus, Trash2, MessageSquare, Table, X, Loader2, Play, ChevronDown, ChevronUp, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface FinanceLeaseProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface LeaseData {
  lease_name: string;
  lease_type: string;
  commencement_date: string;
  lease_period: number;
  int_ror: number;
  payment_cycle: string;
  conditions: {
    c_i: string;
    c_ii: string;
    c_iii: string;
    c_iv: string;
    c_v: string;
  };
  payment_details: PaymentDetail[];
}

interface PaymentDetail {
  payment_classification: string;
  payment_type: string;
  payment_amount: number;
  n_payments: number;
  int_incr: number[];
  incr_per: number[];
  month_first_payment: number;
  increment_pairs: IncrementPair[];
}

interface IncrementPair {
  id: string;
  interval: number;
  percentage: number;
}

interface FinanceLease {
  lease_name: string;
  payment_details: PaymentDetail[];
}

interface LedgerAccount {
  ledger_id: number;
  ledger_name: string;
  closing_balance: number;
  note_line_id: number;
}

interface UserReconciliation {
  reason: string;
  amount: number;
}

export default function FinanceLease({ onBack, setActiveSection }: FinanceLeaseProps) {
  const [financeLeases, setFinanceLeases] = useState<FinanceLease[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRunningAssessment, setIsRunningAssessment] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  
  // Reconciliation table state
  const [liabilityAsPerWorkings, setLiabilityAsPerWorkings] = useState<number>(0);
  const [selectedLedgerAccounts, setSelectedLedgerAccounts] = useState<number[]>([]);
  const [availableLedgerAccounts, setAvailableLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [isLoadingLedgerAccounts, setIsLoadingLedgerAccounts] = useState(false);
  const [userReconciliations, setUserReconciliations] = useState<UserReconciliation[]>([]);
  const [isSavingReconciliation, setIsSavingReconciliation] = useState(false);
  
  // Finance data from Execution_LEASE_FinResponse.json
  const [financeResponseData, setFinanceResponseData] = useState<any>(null);
  const [isLoadingFinanceData, setIsLoadingFinanceData] = useState(false);
  const [isSavingFinanceData, setIsSavingFinanceData] = useState(false);
  const [globalSaveStatus, setGlobalSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Track which leases are expanded
  const [expandedLeases, setExpandedLeases] = useState<Set<string>>(new Set());

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `FINANCE_LEASE_${normalizedSection}`;
  };
  const [jsonData, setJsonData] = useState<any>(null);

  // Download JSON from SharePoint
  const downloadFromSharePoint = async () => {
    setIsLoading(true);
    try {
      if (window.sharePointAPI?.downloadLeaseAssessment) {
        const result = await window.sharePointAPI.downloadLeaseAssessment();
        if (result.success && result.data) {
          setJsonData(result.data);
          
          // Extract Finance leases
          const financeLeasesData = result.data.leases
            .filter((lease: any) => lease.lease_type === 'Finance')
            .map((lease: any) => ({
              lease_name: lease.lease_name,
              payment_details: (lease.payment_details || []).map((detail: any) => ({
                ...detail,
                increment_pairs: detail.int_incr && detail.incr_per 
                  ? detail.int_incr.map((interval: number, index: number) => ({
                      id: `pair_${Date.now()}_${index}`,
                      interval: interval,
                      percentage: detail.incr_per[index] || 0
                    }))
                  : []
              }))
            }));
          
          setFinanceLeases(financeLeasesData);
          console.log('Downloaded Finance leases:', financeLeasesData);
        } else {
          throw new Error(result.error || 'Failed to download lease assessment data');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error downloading from SharePoint:', error);
      alert(`Error downloading from SharePoint: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_LL_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_LL_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "FINANCE-LEASE-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger download on mount
  useEffect(() => {
    downloadFromSharePoint();
    loadLedgerAccounts();
    loadReconciliationData();
    loadFinanceData();
  }, []);

  // Load reconciliation data from Execution_LEASE_FinResponse.json
  const loadReconciliationData = async () => {
    try {
      // Use electron IPC to download the JSON file
      if ((window as any).electronWindow?.ipcRenderer) {
        const result = await (window as any).electronWindow.ipcRenderer.invoke(
          'sharepoint:download-lease-response-json'
        );

        if (result?.success && result.data) {
          const data = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
          if (data.Finance) {
            setLiabilityAsPerWorkings(data.Finance.liabilityAsPerWorkings || 0);
            setSelectedLedgerAccounts(data.Finance.selectedLedgerAccounts || []);
            setUserReconciliations(data.Finance.userReconciliations || []);
          }
        }
      }
    } catch (error) {
      // File doesn't exist yet, which is fine - we'll create it on save
      console.log('Reconciliation file not found, will be created on save');
    }
  };

  // Save reconciliation data to Execution_LEASE_FinResponse.json
  const saveReconciliationData = async () => {
    setIsSavingReconciliation(true);
    try {
      const financeData = {
        liabilityAsPerWorkings,
        selectedLedgerAccounts,
        userReconciliations
      };

      const jsonString = JSON.stringify(financeData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      if (window.sharePointAPI?.uploadFile) {
        await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_LEASE_FinResponse.json',
          referenceValue: 'Lease Liability Reconciliation',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_LEASE_FinResponse']
        });
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving reconciliation data:', error);
    } finally {
      setIsSavingReconciliation(false);
    }
  };

  // Load ledger accounts: AS → Note Line IDs 30040, 30018; Ind AS → 30130, 30148
  const loadLedgerAccounts = async () => {
    setIsLoadingLedgerAccounts(true);
    try {
      let targetNoteLineIds = new Set([30040, 30018]);
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? frfResult.frf : null;
        if (frf === "Indian Accounting Standards (Ind AS)") {
          targetNoteLineIds = new Set([30130, 30148]);
        }
      } catch {
        // keep AS default
      }

      // Fetch trial balance mapping data
      if (window.sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          const accounts: LedgerAccount[] = [];

          // Process all sections (books) in the mapping data
          // Structure: { "Non SEZ": [{ ledger_id, ledger_name, note_line_id, amount: {...} }, ...], ... }
          for (const [sectionName, ledgers] of Object.entries(mappingData)) {
            if (Array.isArray(ledgers)) {
              ledgers.forEach((item: any) => {
                const noteLineId = item.note_line_id || item.noteLineId;
                
                // Filter for Note Line IDs (AS: 30040,30018; Ind AS: 30130,30148)
                if (noteLineId && targetNoteLineIds.has(noteLineId)) {
                  const amountDict = item.amount || {};
                  
                  if (!amountDict || typeof amountDict !== 'object' || Object.keys(amountDict).length === 0) {
                    return; // Skip if no amount data
                  }

                  // Find latest date using the same logic as Python script
                  // Python: max(amount_dict.keys(), key=lambda d: datetime.strptime(d, "%d/%m/%y"))
                  const parseDate = (dateStr: string): Date => {
                    // Format: "dd/mm/yy" -> Date
                    const [day, month, year] = dateStr.split('/');
                    // Convert 2-digit year to 4-digit (25 -> 2025)
                    return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
                  };

                  const dates = Object.keys(amountDict);
                  const latestDate = dates.reduce((latest, current) => {
                    try {
                      const latestDateObj = parseDate(latest);
                      const currentDateObj = parseDate(current);
                      return currentDateObj > latestDateObj ? current : latest;
                    } catch {
                      return latest;
                    }
                  }, dates[0]);

                  const latestAmount = parseFloat(amountDict[latestDate]) || 0;

                  // Clean ledger name (matching Python script logic)
                  // Python: ledger_name.replace("_x000D", "").replace("\n", "").rstrip("_").strip()
                  let ledgerName = (item.ledger_name || item.ledgerName || '').toString();
                  ledgerName = ledgerName
                    .replace(/_x000D/g, '')
                    .replace(/\n/g, '')
                    .replace(/_+$/, '') // Remove trailing underscores
                    .trim();

                  accounts.push({
                    ledger_id: item.ledger_id || item.ledgerId || 0,
                    ledger_name: ledgerName,
                    closing_balance: latestAmount,
                    note_line_id: noteLineId
                  });
                }
              });
            }
          }

          // Remove duplicates based on ledger_id and sum amounts if needed
          const uniqueAccounts = accounts.reduce((acc, account) => {
            const existing = acc.find(a => a.ledger_id === account.ledger_id);
            if (!existing) {
              acc.push(account);
            } else {
              // Sum closing balances if duplicate (same ledger_id appears in multiple sections)
              existing.closing_balance += account.closing_balance;
            }
            return acc;
          }, [] as LedgerAccount[]);

          setAvailableLedgerAccounts(uniqueAccounts);
        }
      }
    } catch (error) {
      console.error('Error loading ledger accounts:', error);
    } finally {
      setIsLoadingLedgerAccounts(false);
    }
  };

  // Calculate total closing balance of selected ledger accounts
  const getTotalClosingBalance = (): number => {
    return selectedLedgerAccounts.reduce((total, ledgerId) => {
      const account = availableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
      return total + (account?.closing_balance || 0);
    }, 0);
  };

  // Calculate difference
  const getDifference = (): number => {
    return liabilityAsPerWorkings - getTotalClosingBalance();
  };

  // Calculate net difference
  const getNetDifference = (): number => {
    const difference = getDifference();
    const reconciliationTotal = userReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);
    return difference - reconciliationTotal;
  };

  // Add user reconciliation row
  const addUserReconciliation = () => {
    setUserReconciliations([...userReconciliations, { reason: '', amount: 0 }]);
  };

  // Remove user reconciliation row
  const removeUserReconciliation = (index: number) => {
    setUserReconciliations(userReconciliations.filter((_, i) => i !== index));
  };

  // Update user reconciliation
  const updateUserReconciliation = (index: number, field: 'reason' | 'amount', value: string | number) => {
    setUserReconciliations(userReconciliations.map((rec, i) => 
      i === index ? { ...rec, [field]: value } : rec
    ));
  };

  // Load Finance data from Execution_LEASE_FinResponse.json
  const loadFinanceData = async () => {
    setIsLoadingFinanceData(true);
    try {
      if (window.sharePointAPI?.downloadLeaseResponseJson) {
        const result = await window.sharePointAPI.downloadLeaseResponseJson();
        if (result.success && result.data) {
          // File now contains Finance data directly, wrapped in Finance key for compatibility
          const financeData = result.data.Finance || {};
          setFinanceResponseData(financeData);
          
          // Populate UI state from loaded data
          if (financeData.liabilityAsPerWorkings !== undefined) {
            setLiabilityAsPerWorkings(financeData.liabilityAsPerWorkings);
          }
          if (financeData.selectedLedgerAccounts !== undefined) {
            setSelectedLedgerAccounts(financeData.selectedLedgerAccounts);
          }
          if (financeData.userReconciliations !== undefined) {
            setUserReconciliations(financeData.userReconciliations);
          }
        } else {
          alert(result.error || 'Failed to load Finance data');
        }
      }
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingFinanceData(false);
    }
  };

  // Save Finance data to Execution_LEASE_FinResponse.json
  const saveFinanceData = async () => {
    setIsSavingFinanceData(true);
    try {
      const financeDataToSave = {
        liabilityAsPerWorkings,
        selectedLedgerAccounts,
        userReconciliations
      };
      
      if (window.sharePointAPI?.uploadLeaseResponseFinance) {
        const result = await window.sharePointAPI.uploadLeaseResponseFinance(financeDataToSave);
        if (result.success) {
          setFinanceResponseData(financeDataToSave);
        } else {
          console.error('Save failed:', result.error);
        }
      }
    } catch (error) {
      console.error('Error saving Finance data:', error);
    } finally {
      setIsSavingFinanceData(false);
    }
  };

  // Add new payment detail for a lease
  const addPaymentDetail = (leaseName: string) => {
    const newPaymentDetail: PaymentDetail = {
      payment_classification: "",
      payment_type: "",
      payment_amount: 0,
      n_payments: 0,
      int_incr: [],
      incr_per: [],
      month_first_payment: 0,
      increment_pairs: []
    };

    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? { ...lease, payment_details: [...lease.payment_details, newPaymentDetail] }
        : lease
    ));
  };

  // Add new increment pair to a payment detail
  const addIncrementPair = (leaseName: string, paymentIndex: number) => {
    const newIncrementPair: IncrementPair = {
      id: Date.now().toString(),
      interval: 0,
      percentage: 0
    };

    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? {
            ...lease,
            payment_details: lease.payment_details.map((detail, i) => 
              i === paymentIndex 
                ? { ...detail, increment_pairs: [...detail.increment_pairs, newIncrementPair] }
                : detail
            )
          }
        : lease
    ));
  };

  // Remove increment pair from a payment detail
  const removeIncrementPair = (leaseName: string, paymentIndex: number, pairId: string) => {
    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? {
            ...lease,
            payment_details: lease.payment_details.map((detail, i) => 
              i === paymentIndex 
                ? { ...detail, increment_pairs: detail.increment_pairs.filter(pair => pair.id !== pairId) }
                : detail
            )
          }
        : lease
    ));
  };

  // Update increment pair
  const updateIncrementPair = (leaseName: string, paymentIndex: number, pairId: string, field: 'interval' | 'percentage', value: number) => {
    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? {
            ...lease,
            payment_details: lease.payment_details.map((detail, i) => 
              i === paymentIndex 
                ? {
                    ...detail,
                    increment_pairs: detail.increment_pairs.map(pair => 
                      pair.id === pairId ? { ...pair, [field]: value } : pair
                    )
                  }
                : detail
            )
          }
        : lease
    ));
  };

  // Remove payment detail for a lease
  const removePaymentDetail = (leaseName: string, index: number) => {
    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? { 
            ...lease, 
            payment_details: lease.payment_details.filter((_, i) => i !== index) 
          }
        : lease
    ));
  };

  // Update payment detail for a lease
  const updatePaymentDetail = (leaseName: string, index: number, field: keyof PaymentDetail, value: any) => {
    setFinanceLeases(prev => prev.map(lease => 
      lease.lease_name === leaseName 
        ? {
            ...lease,
            payment_details: lease.payment_details.map((detail, i) => 
              i === index ? { ...detail, [field]: value } : detail
            )
          }
        : lease
    ));
  };

  // Upload updated JSON to SharePoint
  const uploadToSharePoint = async () => {
    if (!jsonData) {
      console.warn('No JSON data to upload. Please download first.');
      return;
    }

    setIsUploading(true);
    try {
      const updatedJsonData = {
        ...jsonData,
        leases: jsonData.leases.map((lease: any) => {
          const financeLease = financeLeases.find(fl => fl.lease_name === lease.lease_name);
          if (financeLease && lease.lease_type === 'Finance') {
            return {
              ...lease,
              payment_details: financeLease.payment_details.map(detail => ({
                payment_classification: detail.payment_classification,
                payment_type: detail.payment_type,
                payment_amount: detail.payment_amount,
                n_payments: detail.n_payments,
                int_incr: detail.increment_pairs.map(pair => pair.interval),
                incr_per: detail.increment_pairs.map(pair => pair.percentage),
                month_first_payment: detail.month_first_payment
              }))
            };
          }
          return lease;
        })
      };

      const jsonString = JSON.stringify(updatedJsonData, null, 2);
      const base64Content = btoa(jsonString);
      
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }
      
      console.log('Uploading updated Finance lease data to SharePoint:', {
        fileName: 'Execution_LEASE_LeaseAssessment.json',
        folderName: 'juggernaut',
        fyYear: fyYear
      });
      
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_LEASE_LeaseAssessment.json',
          referenceValue: '',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_LEASE_LeaseAssessment']
        });

        console.log('SharePoint upload result:', result);
      } else {
        throw new Error('SharePoint API not available');
      }
      
    } catch (error) {
      console.error('Error uploading to SharePoint:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Run Finance Assessment
  const runFinanceAssessment = async () => {
    setIsRunningAssessment(true);
    try {
      // First upload updated Finance lease data to SharePoint
      await uploadToSharePoint();

      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_finance_lease_assessment", {
          inputFiles: [],
          options: {
            reference_value: "Finance Lease Assessment"
          }
        });

        console.log('Finance Assessment result:', result);

        if (result.ok) {
          alert(' Finance Assessment completed successfully! Excel file has been generated and uploaded to SharePoint.');
          setIsRunningAssessment(false);
        } else {
          throw new Error(result.error || 'Failed to start Finance Assessment');
        }
      } else {
        throw new Error('Payroll API not available');
      }
    } catch (error) {
      console.error('Error running Finance Assessment:', error);
      alert(`Error running Finance Assessment: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      setIsRunningAssessment(false);
    }
  };

  // Global Save: upload JSON, save reconciliation, save inference
  const handleGlobalSave = async () => {
    if (globalSaveStatus === 'saving') {
      return;
    }

    setGlobalSaveStatus('saving');
    try {
      await uploadToSharePoint();
      await saveReconciliationData();
      await saveInferenceQuestion();
      setGlobalSaveStatus('saved');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to perform global save:", error);
      setGlobalSaveStatus('error');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "LL";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "FINANCE-LEASE-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Finance Lease Procedures</h2>
          <p className="text-gray-400">Payment details for Finance leases</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Button
              onClick={handleGlobalSave}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={globalSaveStatus === 'saving' || isRunningAssessment}
            >
              {globalSaveStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            {globalSaveStatus === 'saved' && (
              <span className="text-sm text-green-400">Saved!</span>
            )}
          </div>
          <Button variant="outline" onClick={() => setActiveSection?.("ll-as-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>



      {/* Finance Leases Table */}
      {financeLeases.length > 0 && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-white">
              <FileCheck2 className="h-5 w-5 text-green-500" />
              Finance Lease Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {financeLeases.map((lease, leaseIndex) => {
              const isExpanded = expandedLeases.has(lease.lease_name);
              return (
              <div key={lease.lease_name} className="border border-white/10 rounded-lg bg-white/5">
                <div className="flex items-center justify-between p-4">
                  <button
                    onClick={() => {
                      const newExpanded = new Set(expandedLeases);
                      if (isExpanded) {
                        newExpanded.delete(lease.lease_name);
                      } else {
                        newExpanded.add(lease.lease_name);
                      }
                      setExpandedLeases(newExpanded);
                    }}
                    className="flex items-center gap-2 flex-1 text-left hover:bg-white/5 rounded-lg p-2 -m-2"
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-white" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-white" />
                    )}
                    <h4 className="text-white font-medium text-lg">{lease.lease_name}</h4>
                    <span className="text-white/60 text-sm">
                      ({lease.payment_details.length} payment detail{lease.payment_details.length !== 1 ? 's' : ''})
                    </span>
                  </button>
                  <Button 
                    onClick={() => addPaymentDetail(lease.lease_name)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment Detail
                  </Button>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4">
                    {lease.payment_details.length === 0 ? (
                      <div className="text-center py-4 text-white/60">
                        <p>No payment details added yet. Click "Add Payment Detail" to get started.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {lease.payment_details.map((payment, paymentIndex) => (
                      <div key={paymentIndex} className="border border-white/10 rounded-lg p-4 bg-black/20">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-white font-medium">Payment Detail #{paymentIndex + 1}</h5>
                          <Button 
                            onClick={() => removePaymentDetail(lease.lease_name, paymentIndex)}
                            size="sm"
                            variant="outline"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label className="text-white text-sm">Payment Classification</Label>
                            <Select 
                              value={payment.payment_classification} 
                              onValueChange={(value) => updatePaymentDetail(lease.lease_name, paymentIndex, 'payment_classification', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select classification" />
                              </SelectTrigger>
                              <SelectContent className="border-white/10 bg-black/90 text-white">
                                <SelectItem value="Rental">Rental</SelectItem>
                                <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                                <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-white text-sm">Payment Type</Label>
                            <Input
                              value={payment.payment_type}
                              onChange={(e) => updatePaymentDetail(lease.lease_name, paymentIndex, 'payment_type', e.target.value)}
                              placeholder="Enter payment type"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-white text-sm">Payment Amount</Label>
                            <Input
                              type="number"
                              value={payment.payment_amount}
                              onChange={(e) => updatePaymentDetail(lease.lease_name, paymentIndex, 'payment_amount', parseFloat(e.target.value) || 0)}
                              placeholder="Enter amount"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-white text-sm">No. of Payments</Label>
                            <Input
                              type="number"
                              value={payment.n_payments}
                              onChange={(e) => updatePaymentDetail(lease.lease_name, paymentIndex, 'n_payments', parseInt(e.target.value) || 0)}
                              placeholder="Enter number"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-white text-sm">Increment Intervals & Percentages</Label>
                            <div className="space-y-2">
                              {payment.increment_pairs.map((pair, pairIndex) => (
                                <div key={pair.id} className="flex gap-2 items-center">
                                  <Input
                                    type="number"
                                    value={pair.interval}
                                    onChange={(e) => updateIncrementPair(lease.lease_name, paymentIndex, pair.id, 'interval', parseInt(e.target.value) || 0)}
                                    placeholder="Interval"
                                    className="border-white/10 bg-black/40 text-white flex-1"
                                  />
                                  <Input
                                    type="number"
                                    value={pair.percentage}
                                    onChange={(e) => updateIncrementPair(lease.lease_name, paymentIndex, pair.id, 'percentage', parseFloat(e.target.value) || 0)}
                                    placeholder="Percentage"
                                    className="border-white/10 bg-black/40 text-white flex-1"
                                  />
                                  <Button
                                    onClick={() => removeIncrementPair(lease.lease_name, paymentIndex, pair.id)}
                                    size="sm"
                                    variant="outline"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                onClick={() => addIncrementPair(lease.lease_name, paymentIndex)}
                                size="sm"
                                variant="outline"
                                className="w-full border-white/10 bg-green-500/20 text-white hover:bg-green-500/30"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Increment Pair
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-white text-sm">Month of First Payment</Label>
                            <Input
                              type="number"
                              value={payment.month_first_payment}
                              onChange={(e) => updatePaymentDetail(lease.lease_name, paymentIndex, 'month_first_payment', parseInt(e.target.value) || 0)}
                              placeholder="Enter month"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
            })}
          </CardContent>
        </Card>
      )}

      {/* Run Finance Assessment Button (replaces Upload to SharePoint) */}
      {financeLeases.length > 0 && (
        <div className="flex items-center gap-2">
          <Button 
            onClick={runFinanceAssessment} 
            disabled={isRunningAssessment}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <FileCheck2 className="h-4 w-4 mr-2" />
            {isRunningAssessment ? 'Running Finance Assessment...' : 'Run Finance Assessment'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run Finance Assessment"))}
            className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
            title="Review Comment"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Finance Lease Liability Reconciliation */}
      {financeLeases.length > 0 && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-white">
                <Table className="h-5 w-5" />
                Finance Lease Liability Reconciliation
                </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={loadFinanceData}
                  disabled={isLoadingFinanceData}
                  size="sm"
                  variant="outline"
                  className="border-white/10 text-white"
                >
                  <Play className="h-4 w-4 mr-2" />
                  {isLoadingFinanceData ? 'Loading...' : 'Load Table'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Finance Lease Liability Reconciliation"))}
                  className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                  title="Review Comment"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-white/10">
                <thead>
                  <tr className="bg-black/60">
                    <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                    <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">Finance Lease liability as per workings (A)</td>
                    <td className="border border-white/10 px-4 py-2">
                      <Input
                        type="number"
                        value={liabilityAsPerWorkings || ''}
                        onChange={(e) => setLiabilityAsPerWorkings(parseFloat(e.target.value) || 0)}
                        className="border-white/10 bg-black/40 text-white text-right"
                        placeholder="Enter amount"
                      />
                    </td>
                  </tr>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">Finance Lease liability as per schedule (B)</td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="space-y-2">
                        <Select
                          value=""
                          onValueChange={(value) => {
                            if (value && !selectedLedgerAccounts.includes(parseInt(value))) {
                              setSelectedLedgerAccounts([...selectedLedgerAccounts, parseInt(value)]);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={isLoadingLedgerAccounts ? "Loading..." : "Select ledger accounts"} />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white max-h-60">
                            {availableLedgerAccounts
                              .filter(acc => !selectedLedgerAccounts.includes(acc.ledger_id))
                              .map((account) => (
                                <SelectItem key={account.ledger_id} value={account.ledger_id.toString()}>
                                  {account.ledger_name} (Note Line ID: {account.note_line_id})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {selectedLedgerAccounts.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {selectedLedgerAccounts.map((ledgerId) => {
                              const account = availableLedgerAccounts.find(acc => acc.ledger_id === ledgerId);
                              return account ? (
                                <div key={ledgerId} className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-sm text-white">
                                  <span>{account.ledger_name}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedLedgerAccounts(selectedLedgerAccounts.filter(id => id !== ledgerId))}
                                    className="h-4 w-4 p-0 text-white hover:text-red-400"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                        <div className="text-right text-white font-semibold">
                          Total: {getTotalClosingBalance().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference (C)</td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      {getDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  {userReconciliations.map((rec, index) => (
                    <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">
                        <Input
                          type="text"
                          value={rec.reason}
                          onChange={(e) => updateUserReconciliation(index, 'reason', e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Enter reason"
                        />
                      </td>
                      <td className="border border-white/10 px-4 py-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={rec.amount || ''}
                            onChange={(e) => updateUserReconciliation(index, 'amount', parseFloat(e.target.value) || 0)}
                            className="border-white/10 bg-black/40 text-white text-right flex-1"
                            placeholder="Enter amount"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeUserReconciliation(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      {getNetDifference().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Add Reconciliation Row Button */}
            <div className="flex justify-start items-center">
              <Button
                onClick={addUserReconciliation}
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            {inferenceSaveStatus === 'saved' && (
              <span className="text-sm text-green-400">Saved!</span>
            )}
            {inferenceSaveStatus === 'error' && inferenceSaveError && (
              <span className="text-sm text-red-400">Error</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="lease-liability"
            itemId="FINANCE-LEASE-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Download Finance Lease Data")]: "Download Finance Lease Data",
              [getSectionQuestionId("Finance Lease Payment Details")]: "Finance Lease Payment Details",
              [getSectionQuestionId("Upload Updated Data")]: "Upload Updated Data",
              [getSectionQuestionId("Finance Lease Liability Reconciliation")]: "Finance Lease Liability Reconciliation",
              [getSectionQuestionId("Run Finance Assessment")]: "Run Finance Assessment",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}